// Ingreso con Google por redirección. Todo lo que conoce a Google vive en el
// backend: el client id, el secret y el canje del código. El administrador no
// carga ningún script de Google; solo manda a la persona a la URL que le da el
// backend y, a la vuelta, recoge los tokens de KÄIK.
//
// El viaje completo:
// 1. iniciarSsoGoogle pide a GET {VUE_APP_SSO_BASE_URL}/api/auth/google/url la
//    URL de Google, con redirect_to apuntando al login del admin, y navega a ella.
// 2. Google devuelve a la persona al callback del backend, que canjea el código,
//    valida el usuario y redirige a redirect_to con los tokens o con ?error=.
// 3. Al cargar, el login llama a leerRetornoSso, que saca esos datos de la URL,
//    la deja limpia y los entrega.
//
// Lo que se cuida desde acá porque el backend todavía no lo hace (ver
// claude_docs/Sso/instructivo-backend-sso-google.md en el workspace):
// - redirect_to lleva un valor al azar que también se guarda en sessionStorage, y
//   a la vuelta tienen que coincidir. Así un enlace de regreso armado por otra
//   persona, con los tokens de su propia cuenta, no abre sesión en este navegador.
// - Los datos se sacan de la URL apenas carga el login, para que no queden en la
//   barra ni en la entrada del historial de la pestaña. Hoy el backend los manda
//   en el query string; también se leen del fragmento (#), donde deberían ir.
// - Solo se navega a una URL de accounts.google.com.
//
// Ninguna función lanza: el login tiene que seguir usable con contraseña.

// Base pública de las rutas REST del backend (sin la barra final). Vacía = el
// login no ofrece Google, así el admin puede desplegarse antes que el backend.
const BASE_SSO = (process.env.VUE_APP_SSO_BASE_URL || "").trim().replace(/\/+$/, "");

const ORIGEN_GOOGLE = "https://accounts.google.com";
const CLAVE_PENDIENTE = "kaik_admin_sso";
// Tiempo máximo entre salir a Google y volver
const VIGENCIA_MS = 10 * 60 * 1000;
// Lo que el backend agrega a la URL de regreso, más el valor propio. Se borra
// todo de la URL, también message y email: traen el correo de la persona.
const PARAMETROS_RETORNO = [
  "sso_estado",
  "login_success",
  "access_token",
  "user_token",
  "refresh_token",
  "error",
  "message",
  "email",
];

export const hayGoogleSso = () => Boolean(BASE_SSO);

function valorAlAzar() {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

function guardarPendiente(pendiente) {
  try {
    sessionStorage.setItem(CLAVE_PENDIENTE, JSON.stringify(pendiente));
    return true;
  } catch {
    return false;
  }
}

// Lee y borra el ingreso pendiente: sirve para una sola vuelta
function tomarPendiente() {
  try {
    const crudo = sessionStorage.getItem(CLAVE_PENDIENTE);
    sessionStorage.removeItem(CLAVE_PENDIENTE);
    return crudo ? JSON.parse(crudo) : null;
  } catch {
    return null;
  }
}

/**
 * Manda a la persona a elegir su cuenta de Google, pasando por el backend.
 *
 * @param {Object} opciones
 * @param {String} opciones.urlRegreso - URL absoluta del login del admin.
 * @param {Boolean} opciones.recordarSesion - lo marcado en el login, que se
 * perdería con la recarga si no se guarda.
 * @returns {Promise<null|{error: String|null}>} - null si ya está navegando a
 * Google; { error } con el código para mensajeErrorSso si no se pudo.
 */
export async function iniciarSsoGoogle({ urlRegreso, recordarSesion }) {
  if (!hayGoogleSso()) return { error: "GOOGLE_NOT_CONFIGURED" };

  const estado = valorAlAzar();
  const regreso = new URL(urlRegreso);
  regreso.searchParams.set("sso_estado", estado);
  if (!guardarPendiente({ estado, recordarSesion, creado: Date.now() })) {
    return { error: null };
  }

  const consulta = new URLSearchParams({
    redirect_to: regreso.href,
    // El backend todavía no los lleva hasta el callback; se mandan igual para
    // que funcionen apenas lo haga (instructivo, punto 4)
    tipo_web: "1" /* 1: administrador, 2: intranet */,
    plataforma: "2",
  });

  let urlGoogle;
  try {
    const respuesta = await fetch(`${BASE_SSO}/api/auth/google/url?${consulta}`);
    // 400: faltan las credenciales de Google en el backend. 404: la ruta no está
    // montada (hoy pasa con NODE_ENV=production). Para la persona es lo mismo.
    if (respuesta.status === 400 || respuesta.status === 404) {
      tomarPendiente();
      return { error: "GOOGLE_NOT_CONFIGURED" };
    }
    if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`);
    urlGoogle = new URL((await respuesta.json()).url);
  } catch (error) {
    console.error("No se pudo obtener la URL de Google del backend:", error);
    tomarPendiente();
    return { error: null };
  }

  if (urlGoogle.origin !== ORIGEN_GOOGLE) {
    console.error("El backend devolvió una URL de ingreso que no es de Google");
    tomarPendiente();
    return { error: null };
  }

  window.location.assign(urlGoogle.href);
  return null;
}

/**
 * Recoge la vuelta desde el backend y deja la URL sin los datos que trajo.
 *
 * @returns {null|Object} - null si la URL no es una vuelta del SSO. Si lo es,
 * siempre trae `proveedor` y una de estas formas:
 * - { cancelado: true }: la persona cerró o canceló en Google.
 * - { error }: código para mensajeErrorSso (null = mensaje genérico).
 * - { tokens, recordarSesion }: tokens listos para completarInicioSesion.
 */
export function leerRetornoSso() {
  const url = new URL(window.location.href);
  const fragmento = new URLSearchParams(url.hash.replace(/^#/, ""));
  const leer = (clave) => fragmento.get(clave) ?? url.searchParams.get(clave);

  const estado = leer("sso_estado");
  if (!estado) return null;

  const datos = {};
  PARAMETROS_RETORNO.forEach((clave) => {
    datos[clave] = leer(clave);
    url.searchParams.delete(clave);
    fragmento.delete(clave);
  });
  url.hash = fragmento.toString();
  // Se conserva history.state: ahí guarda vue-router la clave de la entrada
  window.history.replaceState(window.history.state, "", url.pathname + url.search + url.hash);

  const proveedor = "Google";
  const pendiente = tomarPendiente();
  const vigente =
    pendiente && pendiente.estado === estado && Date.now() - pendiente.creado < VIGENCIA_MS;
  if (!vigente) return { proveedor, error: null };

  if (datos.error) {
    if (datos.error === "access_denied") return { proveedor, cancelado: true };
    // El backend manda este en minúsculas cuando redirige
    const error = datos.error === "google_not_configured" ? "GOOGLE_NOT_CONFIGURED" : datos.error;
    return { proveedor, error };
  }

  if (datos.login_success !== "true" || !datos.access_token || !datos.user_token) {
    return { proveedor, error: null };
  }

  return {
    proveedor,
    recordarSesion: Boolean(pendiente.recordarSesion),
    tokens: {
      access_token: datos.access_token,
      user_token: datos.user_token,
      // checkToken exige uuid y la redirección no lo trae; "-" es lo mismo que
      // devolvía usuarioLoginGoogle
      uuid: "-",
    },
  };
}
